import type { FormVariant } from "../types/form-variant";
import type { LeadFormExitMethod } from "../types/lead-form-exit-method";
import type { LeadFormSurface } from "../types/lead-form-surface";

type LeadFormStepEventParams = {
  stepName: string;
  /**
   * Field-keyed object with the values the user entered or selected on this
   * step. Keys are field names (e.g. "careFor", "careType", "urgency").
   * String values for single-value fields; string arrays for multi-select
   * fields. Omitted (defaults to {}) for plain Continue clicks with no
   * tracked inputs.
   */
  userSelection?: Record<string, string | string[]>;
  formVariant: FormVariant;
  /** Sanity careLeadForm versionId (e.g. "v1", "v3-urgency") — sent as form_version_id when present. */
  formVersionId?: string;
  isCorporate: boolean;
  pageZone?: string;
  /** Lead flows only: where the step rendered. Omitted for lead forms. */
  surface?: LeadFormSurface;
};

/**
 * Unified "Lead Form Step" Segment event fired on every meaningful lead-form
 * interaction — radio selection, multi-select continue, and step continue —
 * across both LFB and legacy CareSeeker forms.
 *
 * step_name: stable analytics identifier for the step, resolved as
 *   step.metricsLabel ?? step.id (LFB), step.id (lead flows), or a hardcoded
 *   key (legacy).
 * user_selection: field-keyed object of the values the user entered on this
 *   step. Multi-select fields are arrays; single-value fields are strings.
 *   Empty object {} when there is no selection (plain Continue clicks).
 * form_version_id: present for LFB forms (Sanity versionId); omitted for
 *   legacy forms that carry form_variant instead.
 *
 * page_zone defaults to "sitebuilder" (LFB lives on SiteBuilder pages).
 * Legacy forms may pass a different page_zone (e.g. "ppc").
 */
function buildLeadFormStepEvent({
  stepName,
  userSelection,
  formVariant,
  formVersionId,
  isCorporate,
  pageZone,
  surface,
}: LeadFormStepEventParams) {
  return {
    form_variant: formVariant,
    ...(formVersionId ? { form_version_id: formVersionId } : {}),
    is_corporate: isCorporate,
    lang: "en",
    page_zone: pageZone ?? "sitebuilder",
    step_name: stepName,
    ...(surface ? { surface } : {}),
    user_selection: userSelection ?? {},
  };
}

type LeadFormExitEventParams = {
  stepName: string;
  exitMethod: LeadFormExitMethod;
  formVariant: FormVariant;
  /** Sanity careLeadForm versionId (e.g. "v1", "v3-urgency") — sent as form_version_id when present. */
  formVersionId?: string;
  isCorporate: boolean;
  pageZone?: string;
  /** Lead flows only: the surface the visitor dismissed. Omitted for lead forms. */
  surface?: LeadFormSurface;
};

/**
 * "Lead Form Exit" Segment event fired when the user dismisses the LFB
 * fullscreen takeover, or a lead flow's fullscreen or sheet surface, before
 * completing the form (X button, Escape).
 *
 * step_name: stable analytics identifier for the step the user exited from,
 *   resolved the same way as buildLeadFormStepEvent.
 * exit_method: which dismissal path was used.
 *
 * page_zone defaults to "sitebuilder" (LFB lives on SiteBuilder pages).
 */
function buildLeadFormExitEvent({
  stepName,
  exitMethod,
  formVariant,
  formVersionId,
  isCorporate,
  pageZone,
  surface,
}: LeadFormExitEventParams) {
  return {
    exit_method: exitMethod,
    form_variant: formVariant,
    ...(formVersionId ? { form_version_id: formVersionId } : {}),
    is_corporate: isCorporate,
    lang: "en",
    page_zone: pageZone ?? "sitebuilder",
    step_name: stepName,
    ...(surface ? { surface } : {}),
  };
}

export { buildLeadFormExitEvent, buildLeadFormStepEvent };
